// web-billing-v2 — the web version's purchase obligations (Owner decision list 2026-10-19 item 39; New York
// General Business Law §527-a automatic renewal): before a web subscription is bought, the inspector ticks
// the automatic-renewal terms and the SERVER keeps that consent (with the terms version and the price shown)
// for at least three years; after the purchase the server e-mails an acknowledgment that can be kept; an
// annual web subscription is reminded 15–45 days before it renews. The purchase itself happens on
// RevenueCat's hosted Web Billing page (Stripe) — never in this app.
//
// Shared by the app (src/backend/webBillingClient.ts) and the function (supabase/functions/web-billing-v2).
import type { PlanKind } from './billingPolicy.ts';

export const WEB_BILLING_PROTOCOL_VERSION = 1;
export const WEB_BILLING_FUNCTION_PATH = '/functions/v1/web-billing-v2';

// The terms the consent refers to: docs/legal/TERMS-OF-SERVICE.md and nezha2capital.com/terms-home-inspector.
// Change it together with the terms' version line.
export const WEB_TERMS_VERSION = 'terms-home-inspector-v1-2026-10-20';
export const WEB_TERMS_URL = 'https://nezha2capital.com/terms-home-inspector';

export interface WebConsentRequest {
  action: 'consent';
  protocolVersion: typeof WEB_BILLING_PROTOCOL_VERSION;
  product: PlanKind;
  termsVersion: string;
}

export type WebBillingResponse = { ok: true; protocolVersion: typeof WEB_BILLING_PROTOCOL_VERSION; consentId: number } | { ok: false; protocolVersion: typeof WEB_BILLING_PROTOCOL_VERSION; code: 'unauthenticated' | 'badRequest' | 'notConfigured' | 'internalError'; message: string };

function isRec(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function checkWebConsentRequest(raw: unknown): { ok: true; request: WebConsentRequest } | { ok: false; message: string } {
  if (!isRec(raw)) return { ok: false, message: 'request body is not an object' };
  if (raw.protocolVersion !== WEB_BILLING_PROTOCOL_VERSION) return { ok: false, message: 'unsupported protocolVersion' };
  if (raw.action !== 'consent') return { ok: false, message: 'action must be consent' };
  if (raw.product !== 'monthly' && raw.product !== 'annual') return { ok: false, message: 'product must be monthly or annual' };
  // Only the current terms can be consented to: an old page that still shows earlier terms is refused.
  if (raw.termsVersion !== WEB_TERMS_VERSION) return { ok: false, message: 'terms version is not the current one' };
  return { ok: true, request: { action: 'consent', protocolVersion: WEB_BILLING_PROTOCOL_VERSION, product: raw.product, termsVersion: raw.termsVersion } };
}

export function checkWebBillingResponse(raw: unknown): WebBillingResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== WEB_BILLING_PROTOCOL_VERSION) return null;
  if (raw.ok === true && typeof raw.consentId === 'number' && Number.isFinite(raw.consentId)) return { ok: true, protocolVersion: WEB_BILLING_PROTOCOL_VERSION, consentId: raw.consentId };
  if (raw.ok === false && (raw.code === 'unauthenticated' || raw.code === 'badRequest' || raw.code === 'notConfigured' || raw.code === 'internalError')) {
    return { ok: false, protocolVersion: WEB_BILLING_PROTOCOL_VERSION, code: raw.code, message: typeof raw.message === 'string' ? raw.message : '' };
  }
  return null;
}
